import I18n from 'I18n';
import { InputGroup } from 'foundations-components/transitional/components/InputGroup';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import UISelect from 'UIComponents/input/UISelect';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import { Form } from 'foundations-components/transitional/components/Form';
import { syntheticEvent } from 'foundations-components/transitional/utils/SyntheticEvent';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { ALLOWED_UTM_PARAMETERS, defaultUtmParameters, getDisplayValue, getFullUrl, getProtocol, getUrlBase, getUtmParameters, getUtmUrlInputValidationMessage, mergeUtmParameters } from '../../utils/urlUtils';
import { MEDIA_SIZES, useElementSize } from '../../hooks/useElementSize';
import { INPUT_WIDTHS_PAGE_VIEW_PARAMETERS } from '../../constants/inlineLayout';
import FilterOperatorUtmUrlInput from './FilterOperatorUtmUrlInput';
import styled from 'foundations-theming/utils';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const defaultProtocolValue = 'https://';
const URLInputContainer = styled.div.withConfig({
  displayName: "FilterOperatorUtmUrlInputResponsive__URLInputContainer",
  componentId: "sc-1tcsw4k-0"
})(["display:flex;flex-direction:row;gap:10px;flex-wrap:wrap;align-items:center;"]);
const InternalInputContainer = styled.div.withConfig({
  displayName: "FilterOperatorUtmUrlInputResponsive__InternalInputContainer",
  componentId: "sc-1tcsw4k-1"
})(({
  size
}) => ({
  width: `${INPUT_WIDTHS_PAGE_VIEW_PARAMETERS[size]}px`
}));
const UTMParameterInputContainer = styled(InternalInputContainer).withConfig({
  displayName: "FilterOperatorUtmUrlInputResponsive__UTMParameterInputContainer",
  componentId: "sc-1tcsw4k-2"
})(["display:flex;align-items:center;gap:8px;"]);
export default function FilterOperatorUtmUrlInputResponsive(props) {
  const {
    disabled,
    error,
    value,
    onBlur,
    onChange,
    inputUse
  } = props;
  const isError = error.get('error');
  const errorMessage = error.get('message');
  const {
    width: {
      size
    }
  } = useElementSize();
  const protocol = !disabled && getProtocol(value.urlBase) || defaultProtocolValue;
  const urlDomain = !disabled ? getDisplayValue(value.urlBase) : '';
  const utmParameters = !disabled ? Object.assign({}, value.utmParameters) : Object.assign({}, defaultUtmParameters);
  const handleProtocolChange = evt => {
    const newProtocol = evt.target.value;
    const newValue = getFullUrl(urlDomain, newProtocol);
    onChange(syntheticEvent({
      urlBase: newValue,
      utmParameters
    }));
  };
  const handleDomainChange = evt => {
    const {
      target: {
        value: currentValue
      }
    } = evt;
    const nextProtocol = getProtocol(currentValue) || protocol || defaultProtocolValue;
    const nextDisplayValue = getDisplayValue(currentValue);
    onChange(syntheticEvent({
      urlBase: getFullUrl(nextDisplayValue, nextProtocol),
      utmParameters
    }));
  };
  const handleParameterChange = (id, evt) => {
    const {
      target: {
        value: evtValue
      }
    } = evt;
    utmParameters[id] = evtValue;
    onChange(syntheticEvent({
      urlBase: getFullUrl(urlDomain, protocol),
      utmParameters
    }));
  };
  const handleFixClick = () => {
    const newDomain = getUrlBase(urlDomain);
    const newUtmParameters = getUtmParameters(getFullUrl(urlDomain, protocol));
    onChange(syntheticEvent({
      urlBase: getFullUrl(newDomain, protocol),
      utmParameters: mergeUtmParameters(newUtmParameters, utmParameters)
    }));
  };
  if (size === MEDIA_SIZES.sm) {
    return /*#__PURE__*/_jsx(FilterOperatorUtmUrlInput, Object.assign({}, props));
  }
  const getUtmBlock = id => {
    return /*#__PURE__*/_jsxs(UTMParameterInputContainer, {
      size: size,
      children: [/*#__PURE__*/_jsx(View, {
        inlineSize: "30%",
        children: /*#__PURE__*/_jsx(Text, {
          typography: "body-100",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `customerDataFiltersUI.customerDataFilters.FilterOperatorUtmUrlInput.parameters.${id}`
          })
        })
      }), /*#__PURE__*/_jsx(FormControl, {
        "aria-label": id,
        children: /*#__PURE__*/_jsx(TextInput, {
          onBlur: () => {
            if (!disabled && onBlur) {
              onBlur();
            }
          },
          onChange: evt => handleParameterChange(id, evt),
          value: utmParameters[id],
          disabled: disabled,
          use: inputUse
        })
      })]
    }, id);
  };
  return /*#__PURE__*/_jsx(Form, {
    children: /*#__PURE__*/_jsx(FormControl, {
      "aria-label": I18n.text('customerDataFiltersUI.customerDataFilters.FilterOperatorInput.ariaLabel'),
      error: !disabled && isError,
      validationMessage: !disabled && isError ? /*#__PURE__*/_jsxs(_Fragment, {
        children: [errorMessage, ' ', getUtmUrlInputValidationMessage(urlDomain, utmParameters, handleFixClick)]
      }) : null,
      children: /*#__PURE__*/_jsxs(URLInputContainer, {
        children: [/*#__PURE__*/_jsx(InternalInputContainer, {
          size: size,
          children: /*#__PURE__*/_jsxs(InputGroup, {
            required: true,
            use: "itemLeft",
            disabled: disabled,
            marginBlock: "200",
            children: [/*#__PURE__*/_jsx(FormControl, {
              "aria-label": I18n.text('customerDataFiltersUI.customerDataFilters.FilterOperatorInput.ariaLabel'),
              children: /*#__PURE__*/_jsx(UISelect, {
                buttonUse: inputUse === 'on-dark' ? 'form-on-dark' : undefined,
                defaultValue: defaultProtocolValue,
                onChange: handleProtocolChange,
                options: [{
                  text: 'https://',
                  value: 'https://'
                }, {
                  text: 'http://',
                  value: 'http://'
                }],
                value: protocol
              })
            }), /*#__PURE__*/_jsx(FormControl, {
              "aria-label": I18n.text('customerDataFiltersUI.customerDataFilters.FilterOperatorInput.ariaLabel'),
              error: !disabled && isError,
              children: /*#__PURE__*/_jsx(TextInput, {
                onBlur: () => {
                  if (!disabled && onBlur) {
                    onBlur();
                  }
                },
                onChange: handleDomainChange,
                value: urlDomain,
                use: inputUse
              })
            })]
          })
        }), ALLOWED_UTM_PARAMETERS.map(param => getUtmBlock(param))]
      })
    })
  });
}